import { endOfDay, startOfDay } from 'date-fns'

import arriereBackoffice, {
  CreateReplyArgs,
  CreateTicketArgs,
  SortDirection,
  TicketPriority,
  TicketSelection,
  TicketSortField,
  TicketStatus,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

export const getTicketsSummaryByDate = async ({
  period,
  requester,
}: {
  period: { startDate: string; endDate: string }
  requester?: string
}) => {
  return arriereBackoffice.queries.ticketSummaryByDate({
    __alias: 'getTicketsSummaryByDate',
    __args: {
      filter: {
        ...period,
        requesterIds: requester ? [requester] : undefined,
      },
    },
    date: true,
    open: true,
    inProgress: true,
    waitingAnswer: true,
    resolved: true,
    total: true,
  })
}

export const getRevenueTickets = async ({
  sort,
  date,
  period,
  status,
  term,
  assignee,
  requester,
  marketIds,
  propertyIds,
  page = 1,
  pageSize = 10,
}: {
  sort?: { field: TicketSortField; direction: SortDirection }
  period?: { startDate: string; endDate: string }
  date?: string
  status?: TicketStatus
  term?: string
  assignee?: string
  requester?: string
  marketIds?: string[]
  propertyIds?: string[]
  page?: number | string
  pageSize?: number
}) => {
  page = Number(page)
  if (date && !period) {
    const [y, m, d] = date.split('-').map(Number)
    period = {
      startDate: startOfDay(new Date(y, m - 1, d)).toISOString(),
      endDate: endOfDay(new Date(y, m - 1, d)).toISOString(),
    }
  }
  return arriereBackoffice.queries
    .tickets({
      __args: {
        filter: {
          period,
          status: status ? [status] : [],
          sort: sort ? sort : { field: TicketSortField.status, direction: SortDirection.asc },
          term,
          page,
          pageSize,
          assigneesIds: assignee ? [assignee] : [],
          requesterIds: requester ? [requester] : undefined,
          marketIds,
          propertyIds,
        },
      },
      __alias: 'getRevenueTickets',
      pages: true,
      total: true,
      data: {
        displayDescription: true,
        home: {
          name: true,
          region: { name: true },
          thumbnail: {
            url: true,
          },
          address: {
            address: true,
            zipCode: true,
            city: true,
            state: { name: true },
          },
        },
        assigneeId: true,
        assigneeName: true,
        createdAt: true,
        customFields: true,
        displayId: true,
        domain: { code: true, name: true, slug: true, createdAt: true, description: true },
        id: true,
        priority: true,
        propertyId: true,
        propertyName: true,
        assets: {
          id: true,
          context: true,
          fileName: true,
          url: true,
          size: true,
        },
        requestType: {
          id: true,
          createdAt: true,
          description: true,
          name: true,
          slug: true,
        },
        requesterAvatarUrl: true,
        requesterDepartment: true,
        requesterEmail: true,
        requesterId: true,
        requesterJobTitle: true,
        requesterName: true,
        resolvedAt: true,
        status: true,
        updatedAt: true,
      },
      page: true,
    })
    .then(({ pages, total, data }) => ({ pages, total, data: data ?? [] }))
}

export const getTicketByDisplayId = async (displayId: string) => {
  const query = {
    assetIds: true,
    assets: {
      id: true,
      context: true,
      fileName: true,
      url: true,
      size: true,
    },
    assigneeId: true,
    assigneeName: true,
    assignee: {
      id: true,
      name: true,
      email: true,
    },
    createdAt: true,
    customFields: true,
    displayDescription: true,
    displayId: true,
    home: {
      name: true,
      region: { name: true },
      thumbnail: {
        url: true,
      },
      address: {
        address: true,
        zipCode: true,
        city: true,
        state: { name: true },
      },
    },
    id: true,
    marketId: true,
    marketName: true,
    priority: true,
    propertyId: true,
    propertyName: true,
    requestType: {
      id: true,
      description: true,
      name: true,
    },
    requesterAvatarUrl: true,
    requesterDepartment: true,
    requesterEmail: true,
    requesterId: true,
    requesterJobTitle: true,
    requesterName: true,
    resolvedAt: true,
    status: true,
    updatedAt: true,
    timeline: {
      id: true,
      actorId: true,
      actorName: true,
      actorAvatarUrl: true,
      createdAt: true,
      eventType: true,
      assets: {
        id: true,
        context: true,
        fileName: true,
        url: true,
        size: true,
      },
      message: true,
      payload: {
        statusChange: {
          from: true,
          to: true,
        },
        assigneeChange: {
          fromId: true,
          fromName: true,
          toId: true,
          toName: true,
        },
        fieldChanges: {
          field: true,
          label: true,
          oldValue: true,
          newValue: true,
        },
        priorityChange: {
          from: true,
          to: true,
        },
      },
    },
  } satisfies TicketSelection
  return arriereBackoffice.queries.ticket({
    __alias: 'getTicketByDisplayId',
    __args: { displayId },
    ...query,
  })
}

export const getTicketDomainAnalysts = async (code = 'RM') => {
  return arriereBackoffice.queries
    .ticketDomain({
      __alias: 'getTicketDomainAnalysts',
      __args: { code },
      assigneeOptions: {
        id: true,
        name: true,
        markets: true,
      },
    })
    .then(domain =>
      (domain.assigneeOptions ?? [])
        .filter(a => a.id && a.name && (a.markets ?? []).length)
        .map(a => ({ id: a.id!, name: a.name!, markets: a.markets ?? [] }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    )
}

export const getTicketDomain = async (code = 'RM') => {
  return arriereBackoffice.queries.ticketDomain({
    __alias: 'getTicketDomain',
    __args: {
      code,
    },
    assigneeOptions: {
      id: true,
      name: true,
      user: {
        email: true,
      },
      markets: true,
    },
    code: true,
    createdAt: true,
    id: true,
    name: true,
    slug: true,
    displayIdPrefix: true,
    description: true,
    isActive: true,
    updatedAt: true,
    sortOrder: true,
    requestTypes: {
      id: true,
      createdAt: true,
      name: true,
      slug: true,
      domainId: true,
      description: true,
      isActive: true,
      updatedAt: true,
      sortOrder: true,
      fields: {
        id: true,
        type: true,
        label: true,
        name: true,
        placeholder: true,
        fieldId: true,
        helpText: true,
        isRequired: true,
        options: {
          label: true,
          value: true,
        },
        sortOrder: true,
        validationRules: true,
      },
    },
  })
}

export const createTicket = async (input: CreateTicketArgs['input']) => {
  return arriereBackoffice.mutations.createTicket({
    __alias: 'createTicket',
    __args: {
      input,
    },
    id: true,
    createdAt: true,
    displayId: true,
  })
}

export const reopenTicket = async ({
  ticketId,
  authorAvatarUrl,
}: {
  ticketId: string
  authorAvatarUrl?: string
}) => {
  return arriereBackoffice.mutations.reopenTicket({
    __alias: 'reopenTicket',
    __args: { id: ticketId, author: { avatarUrl: authorAvatarUrl } },
    id: true,
  })
}

export const updateTicketStatus = async ({
  ticketId,
  status,
  authorAvatarUrl,
}: {
  ticketId: string
  status: TicketStatus
  authorAvatarUrl?: string
}) => {
  return arriereBackoffice.mutations.updateTicket({
    __alias: 'updateTicketStatus',
    __args: { id: ticketId, input: { status, author: { avatarUrl: authorAvatarUrl } } },
    id: true,
    status: true,
  })
}

export const reassignTicket = async ({
  id,
  priority,
  assignee,
  authorAvatarUrl,
}: {
  id: string
  priority: TicketPriority
  assignee: { id: string; name?: string; email: string }
  authorAvatarUrl?: string
}) => {
  return arriereBackoffice.mutations.updateTicket({
    __alias: 'reassignTicket',
    __args: {
      id,
      input: {
        priority,
        assignee,
        author: { avatarUrl: authorAvatarUrl },
      },
    },
    id: true,
  })
}

export const resolveTicket = async ({
  ticketId,
  authorAvatarUrl,
}: {
  ticketId: string
  authorAvatarUrl?: string
}) => {
  return arriereBackoffice.mutations.resolveTicket({
    __alias: 'resolveTicket',
    __args: { id: ticketId, author: { avatarUrl: authorAvatarUrl } },
    id: true,
  })
}

export const sendTicketReply = async (input: CreateReplyArgs['input']) => {
  return arriereBackoffice.mutations.createReply({
    __alias: 'createReply',
    __args: {
      input,
    },
    id: true,
  })
}

export const getCommentMentionOptions = async (terms: string) => {
  return arriereBackoffice.queries.usersToTag({
    __alias: 'getCommentMentionOptions',
    __args: { terms },
    id: true,
    name: true,
    email: true,
  })
}
